import React, { useState, useEffect, useCallback, useRef } from "react";
import Icon from "./components/Icon";
import { useMsal } from "@azure/msal-react";

import Toolbar from "./components/Toolbar";
import FileListView from "./components/FileListView";
import FileGridView from "./components/FileGridView";
import ActivityPanel from "./components/ActivityPanel";
import NewFolderModal from "./components/NewFolderModal";
import UploadModal from "./components/UploadModal";
import CreateDocumentModal from "./components/CreateDocumentModal";
import FilePreviewModal from "./components/FilePreviewModal";
import ManageAccessModal from "./components/ManageAccessModal";
import RenameModal from "./components/RenameModal";
import ConfirmModal from "./components/ConfirmModal";
import DragDropOverlay from "./components/DragDropOverlay";
import ContextMenu from "./components/ContextMenu";
import EmptyState from "./components/EmptyState";
import * as api from "../../services/api";
import "./ctodocx.css";

function CTODOCXApp({ user }) {
  const { instance } = useMsal();
  const [fileSystem, setFileSystem] = useState({});
  const [currentPath, setCurrentPath] = useState("/");
  const [viewMode, setViewMode] = useState("list");

  const [sortBy, setSortBy] = useState("name");
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateDocModal, setShowCreateDocModal] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [showManageAccess, setShowManageAccess] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, item: null });
  const [dragOverMain, setDragOverMain] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ show: false, title: "", message: "", onConfirm: null });

  // Highlighted item id coming from a shared file link (?item= or ?share= resolution)
  const [highlightId, setHighlightId] = useState(null);

  // Track whether share link resolution is in progress
  const shareResolving = useRef(false);

  // Initialize API service + handle incoming share links
  useEffect(() => {
    if (!instance) return;
    api.initApiService(instance);

    const params = new URLSearchParams(window.location.search);
    let shareToken = params.get("share");

    // Fallback: check localStorage for share token preserved across MSAL redirect
    if (!shareToken) {
      shareToken = localStorage.getItem("ctodocx_pending_share");
    }

    // Clean up: remove from URL and localStorage so refresh doesn't re-trigger
    if (shareToken) {
      window.history.replaceState({}, "", window.location.pathname);
      localStorage.removeItem("ctodocx_pending_share");
    }

    if (shareToken) {
      // ─── Token-based share link: resolve token → navigate to file ───
      shareResolving.current = true;
      api.resolveShareLink(shareToken)
        .then((data) => {
          if (data.success) {
            const targetPath = data.path || "/";
            setCurrentPath(targetPath);
            if (data.file_id) setHighlightId(data.file_id);
            loadFolder(targetPath);
            showToast("success", `Shared file: "${data.file_name || "Document"}"`);
          } else {
            showToast("error", data.message || "Share link is invalid, expired, or you don't have permission.");
            loadFolder("/");
          }
        })
        .catch((err) => {
          showToast("error", err.message || "Could not resolve share link");
          loadFolder("/");
        })
        .finally(() => { shareResolving.current = false; });
    } else {
      loadFolder("/");
    }
  }, [instance]);

  // Auto-scroll + highlight the shared file after folder loads
  useEffect(() => {
    if (!highlightId || loading) return;
    // Wait for DOM to render after loading state clears
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-file-id="${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("shared-highlight");
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [highlightId, loading, fileSystem]);

  // Auto-clear highlight after 5 seconds
  useEffect(() => {
    if (highlightId) {
      const timer = setTimeout(() => {
        const el = document.querySelector(".shared-highlight");
        if (el) el.classList.remove("shared-highlight");
        setHighlightId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightId]);

  // Close context menu on click
  useEffect(() => {
    const close = () => setContextMenu({ show: false, x: 0, y: 0, item: null });
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // Handle browser back/forward button for folder navigation
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.ctodocxPath !== undefined) {
        // Navigate to the previous folder from browser history
        const path = event.state.ctodocxPath;
        setCurrentPath(path);
        setSearchQuery("");
        setSearchResults(null);
        loadFolder(path);
      } else {
        // No ctodocx state = user went back before first folder navigation
        // Stay in app at root instead of leaving
        setCurrentPath("/");
        setSearchQuery("");
        setSearchResults(null);
        loadFolder("/");
        // Replace current state (not push!) to avoid infinite history entries
        window.history.replaceState({ ctodocxPath: "/" }, "", "");
      }
    };

    // Set initial history state so first back press doesn't leave app
    window.history.replaceState({ ctodocxPath: currentPath }, "", "");

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), toast.type === "error" ? 6000 : 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ═══════════════════════════════════════════════════
  // LOAD FOLDER — Parallel fetch folders + documents
  // ═══════════════════════════════════════════════════
  const loadFolder = useCallback(async (path) => {
    try {
      setLoading(true);
      const items = [];

      const [folderData, docData] = await Promise.all([
        api.listFolder(path).catch((err) => {
          if (err.message && (err.message.includes("403") || err.message.includes("Forbidden"))) {
            showToast("error", "Access denied — you don\u2019t have permission to view this folder.");
          }
          return { items: [], folders: [] };
        }),
        api.listDocuments(path).catch((err) => {
          return { documents: [] };
        }),
      ]);

      // Parse folders
      const folderItems = folderData.items || folderData.folders || [];
      folderItems.forEach((item) => {
        const name = typeof item === "string" ? item : (item.name || item.display_name || item.folder_name || "");
        // id must be the folder's FULL PATH (matches backend folder_full_path,
        // e.g. "/Projects/Q3") — not just the folder name. The backend keys
        // permissions/activity/share records by full path, so a bare name
        // here breaks Activity Log, shared-link highlighting, etc. for folders.
        const folderId = path === "/" ? `/${name}` : `${path}/${name}`;
        items.push({
          type: "folder",
          name: name,
          path: path,
          modified: item.created_date || item.updated_date || item.createdDate || "",
          owner: item.createdByName || item.created_by_name || item.owner || "",
          id: folderId,
        });
      });

      // Parse documents — FIXED: proper field mapping from backend response
      const docs = docData.documents || docData.items || [];
      docs.forEach((doc) => {
        const name = doc.fileName || doc.display_name || doc.name || "";
        items.push({
          type: "file",
          name: name,
          ext: name.includes(".") ? name.split(".").pop().toLowerCase() : "",
          size: doc.fileSize ? formatBytes(doc.fileSize) : (doc.file_size ? formatBytes(doc.file_size) : ""),
          modified: doc.createdDate || doc.created_date || doc.updatedDate || doc.updated_date || "",
          owner: doc.createdByName || doc.created_by_name || doc.owner || "",
          restricted: doc.visibility === "private" || doc.is_restricted || false,
          id: doc.id || doc.item_id,
          s3_key: doc.s3Key || doc.s3_key || "",
          description: doc.description || "",
          status: doc.status || "active",
          contentType: doc.contentType || doc.content_type || "",
        });
      });

      setFileSystem((prev) => ({ ...prev, [path]: items }));
    } catch (error) {
      setFileSystem((prev) => ({ ...prev, [path]: [] }));
      showToast("error", `Failed to load: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const navigateTo = useCallback((path) => {
    setCurrentPath(path);
    setSearchQuery("");
    setSearchResults(null);
    loadFolder(path);
    // Push folder path to browser history so back button navigates folders
    window.history.pushState({ ctodocxPath: path }, "", "");
  }, [loadFolder]);

  // ═══════════════════════════════════════════════════
  // SEARCH — Real API with debounce
  // ═══════════════════════════════════════════════════
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.searchDocuments(searchQuery);
        const docs = res.documents || res.results || res.items || [];
        const results = docs.map((doc) => {
          const name = doc.fileName || doc.display_name || doc.name || "";
          return {
            type: "file",
            name: name,
            ext: name.includes(".") ? name.split(".").pop().toLowerCase() : "",
            size: doc.fileSize ? formatBytes(doc.fileSize) : "",
            modified: doc.createdDate || doc.created_date || "",
            owner: doc.createdByName || doc.created_by_name || "",
            id: doc.id || doc.item_id,
            location: doc.folderPath || doc.folder_path || "/",
            restricted: false,
          };
        });
        setSearchResults(results);
      } catch (err) {
        // Fallback: local search over already-loaded items
        const seen = new Set();
        const localResults = [];
        Object.keys(fileSystem).forEach((path) => {
          (fileSystem[path] || []).forEach((item) => {
            if (item.type === "folder") return;
            if (!item.id) return;
            if (seen.has(item.id)) return;
            if (item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
              seen.add(item.id);
              localResults.push({ ...item, location: path });
            }
          });
        });
        setSearchResults(localResults);
      } finally {
        setSearchLoading(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Get files to display
  const getFiles = useCallback(() => {
    let files = searchResults !== null ? searchResults : (fileSystem[currentPath] || []);
    // Apply filter
    if (filter === "folders") files = files.filter(f => f.type === "folder");
    if (filter === "documents") files = files.filter(f => f.type !== "folder");
    return [...files].sort((a, b) => {
      if (a.type === "folder" && b.type !== "folder") return -1;
      if (a.type !== "folder" && b.type === "folder") return 1;
      switch (sortBy) {
        case "name": return (a.name || "").localeCompare(b.name || "");
        case "date": return (b.modified || "").localeCompare(a.modified || "");
        case "size": return (parseSize(b.size) || 0) - (parseSize(a.size) || 0);
        case "type": return (a.ext || "").localeCompare(b.ext || "");
        default: return 0;
      }
    });
  }, [fileSystem, currentPath, searchResults, sortBy, filter]);

  // ═══════════════════════════════════════════════════
  // ACTIONS — with activity logging
  // ═══════════════════════════════════════════════════
  const createFolder = async (name) => {
    try {
      await api.createFolder(currentPath, name);
      showToast("success", `Folder "${name}" created!`);
      await loadFolder(currentPath);
      setShowNewFolderModal(false);
    } catch (error) {
      showToast("error", `Failed: ${error.message}`);
    }
  };

  const handleUpload = async (uploadedFiles, rawFiles) => {
    let successCount = 0;
    const failed = [];
    const totalFiles = (rawFiles || []).length;

    for (const file of rawFiles || []) {
      try {
        showToast("info", `Uploading ${file.name} (${successCount + 1}/${totalFiles})...`);
        await api.uploadFile(file, currentPath, () => {});
        successCount++;
      } catch (err) {
        failed.push(file.name);
      }
    }

    if (successCount > 0) {
      showToast("success", `${successCount} file(s) uploaded successfully!`);
      await loadFolder(currentPath);
    }
    if (failed.length > 0) {
      showToast("error", `Failed to upload: ${failed.join(", ")}`);
    }
  };

  const handleCreateDocument = async (newFile) => {
    try {
      const contentTypeMap = {
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        pdf: "application/pdf",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        txt: "text/plain",
      };
      const contentType = contentTypeMap[newFile.ext] || "application/octet-stream";
      const fileSize = parseInt(newFile.size) || 100;

      // Step 1: Create document (gets presigned URL from backend)
      const result = await api.createDocument(newFile.name, currentPath, contentType, fileSize);
      const docId = result.document?.id || result.documentId || result.document?.item_id;

      if (!docId) throw new Error("No document ID returned");

      // Step 2: Get presigned upload URL
      let presignedUrl = result.uploadUrl;
      if (!presignedUrl) {
        const uploadResult = await api.getUploadUrl(newFile.name, currentPath, contentType, docId);
        presignedUrl = uploadResult.uploadUrl || uploadResult.url || (uploadResult.upload && uploadResult.upload.url);
      }

      if (!presignedUrl) throw new Error("No upload URL returned");

      // Step 3: Upload placeholder content
      const userName = api.getCurrentUser()?.name || user?.name || "User";
      const placeholderContent = `${newFile.name}\nCreated by: ${userName}\nDate: ${new Date().toISOString()}\n\n(Empty document - download and edit)`;
      const fileBlob = new Blob([placeholderContent], { type: contentType });

      await fetch(presignedUrl, {
        method: "PUT",
        body: fileBlob,
        headers: { "Content-Type": contentType },
      });

      // Step 4: Confirm upload — flips status from "pending_upload" to "active"
      await api.confirmUpload(docId, currentPath);

      // Step 5: Log activity
      // Activity logged by backend on create

      showToast("success", `"${newFile.name}" created successfully!`);
      await loadFolder(currentPath);
    } catch (error) {
      showToast("error", `Create failed: ${error.message}`);
    }
  };

  const handleDownload = async (file) => {
    if (file.restricted) {
      showToast("error", `"${file.name}" is restricted.`);
      return;
    }
    try {
      api.logActivity(file.id, "downloaded", `File downloaded: ${file.name}`).catch(() => {});
      await api.downloadFile(file.id, file.name);
      showToast("success", `"${file.name}" download started`);
    } catch (err) {
      showToast("error", `Download failed: ${err.message}`);
    }
  };

  const handleFileClick = (item) => {
    if (item.type === "folder") {
      const path = currentPath === "/" ? `/${item.name}` : `${currentPath}/${item.name}`;
      navigateTo(path);
    } else {
      setSelectedFile(item);
      setPreviewFile(item);
    }
  };

  // Context menu
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ show: true, x: e.clientX, y: e.clientY, item });
  };

  const handleContextAction = async (action, item) => {
    setContextMenu({ show: false, x: 0, y: 0, item: null });

    switch (action) {
      case "open":
        handleFileClick(item);
        break;

      case "download":
        await handleDownload(item);
        break;

      case "share": {
        // Token-based share link — creates a backend-managed shareable URL.
        // item.id is now always the correct identifier: full path for
        // folders, UUID for files (see loadFolder()).
        try {
          showToast("info", "Creating share link...");
          const data = await api.createShareLink(item.id, { expires_days: 30 });
          if (data.success && data.shareUrl) {
            try {
              await navigator.clipboard.writeText(data.shareUrl);
              showToast("success", "Share link copied! Only users with access permission can view (expires in 30 days).");
            } catch {
              window.prompt("Copy this share link:", data.shareUrl);
            }
          } else {
            showToast("error", data.message || "Failed to create share link");
          }
        } catch (err) {
          showToast("error", `Share link failed: ${err.message}`);
        }
        break;
      }

      case "rename":
        setSelectedFile(item);
        setShowRenameModal(true);
        break;

      case "restrict":
      case "permissions":
        setSelectedFile(item);
        setShowManageAccess(true);
        break;

      case "activity":
        setSelectedFile(item);
        setShowActivityPanel(true);
        break;

      case "delete":
        if (item.type === "folder") {
          setConfirmModal({
            show: true,
            title: "Delete Folder",
            message: `Delete folder "${item.name}" and all its contents? This cannot be undone.`,
            danger: true,
            onConfirm: async () => {
              setConfirmModal({ show: false });
              try {
                await api.deleteFolder(item.name, currentPath);
                showToast("success", `Folder "${item.name}" deleted`);
                await loadFolder(currentPath);
              } catch (err) {
                showToast("error", `Delete failed: ${err.message}`);
              }
            },
          });
        } else {
          setConfirmModal({
            show: true,
            title: "Delete File",
            message: `Delete "${item.name}"? This cannot be undone.`,
            danger: true,
            onConfirm: async () => {
              setConfirmModal({ show: false });
              try {
                await api.deleteDocument(item.id);
                showToast("success", `"${item.name}" deleted`);
                await loadFolder(currentPath);
              } catch (err) {
                showToast("error", `Delete failed: ${err.message}`);
              }
            },
          });
        }
        break;

      case "details":
        setPreviewFile(item);
        break;

      default:
        showToast("info", `${action}: coming soon`);
    }
  };

  const showToast = (type, message) => {
    setToast({ type, message });
  };

  // Drag & drop
  const handleMainDragOver = (e) => { e.preventDefault(); setDragOverMain(true); };
  const handleMainDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) setDragOverMain(false);
  };
  const handleMainDrop = (e) => { e.preventDefault(); setDragOverMain(false); setShowUploadModal(true); };

  const files = getFiles();

  return (
    <div className="ctodocx-app">
      {/* Sidebar removed */}
{/* Mobile backdrop */}
<div
        className="ctodocx-main"
        onDragOver={handleMainDragOver}
        onDragLeave={handleMainDragLeave}
        onDrop={handleMainDrop}
      >
        <Toolbar
          viewMode={viewMode}
          onViewChange={setViewMode}
          sortBy={sortBy}
          onSortChange={setSortBy}
          filter={filter}
          onFilterChange={setFilter}
          onNewFolder={() => setShowNewFolderModal(true)}
          onUpload={() => setShowUploadModal(true)}
          onCreateDocument={() => setShowCreateDocModal(true)}
          onToggleActivity={() => {
            setShowActivityPanel(!showActivityPanel);
          }}
          searchQuery={searchQuery}
          onSearchChange={(val) => {
            setSearchQuery(val);
            if (!val) setSearchResults(null);
          }}
          searchLoading={searchLoading}
          currentPath={currentPath}
          onNavigate={navigateTo}
          folderCount={(() => {
            let count = 0;
            Object.values(fileSystem).forEach(items =>
              (items || []).forEach(item => { if (item.type === "folder") count++; })
            );
            return count;
          })()}
          fileCount={(() => {
            let count = 0;
            Object.values(fileSystem).forEach(items =>
              (items || []).forEach(item => { if (item.type !== "folder") count++; })
            );
            return count;
          })()}
        />

        <div className="ctodocx-file-area">
          {loading ? (
            <div className="loading-state">
              <Icon name="spinner" size={18} className="fa-spin" />
              <p>Loading...</p>
            </div>
          ) : files.length === 0 ? (
            <EmptyState searchQuery={searchQuery} />
          ) : viewMode === "list" ? (
            <FileListView
              files={files}
              currentPath={currentPath}
              onNavigate={navigateTo}
              onFileClick={handleFileClick}
              onContextMenu={handleContextMenu}
              isSearch={searchResults !== null}
              highlightId={highlightId}
            />
          ) : (
            <FileGridView
              files={files}
              currentPath={currentPath}
              onNavigate={navigateTo}
              onFileClick={handleFileClick}
              onContextMenu={handleContextMenu}
              highlightId={highlightId}
            />
          )}
        </div>

        <DragDropOverlay visible={dragOverMain} />
      </div>

      {/* Panels */}
      <ActivityPanel
        show={showActivityPanel}
        onClose={() => setShowActivityPanel(false)}
        selectedFile={selectedFile}
        showToast={showToast}
      />

{/* Modals */}
      {showNewFolderModal && (
        <NewFolderModal
          onClose={() => setShowNewFolderModal(false)}
          onCreate={createFolder}
        />
      )}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
          currentPath={currentPath}
        />
      )}
      {showCreateDocModal && (
        <CreateDocumentModal
          onClose={() => setShowCreateDocModal(false)}
          onCreate={handleCreateDocument}
          currentPath={currentPath}
        />
      )}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={handleDownload}
          showToast={showToast}
        />
      )}
      {showManageAccess && selectedFile && (
        <ManageAccessModal
          file={selectedFile}
          onClose={() => { setShowManageAccess(false); setSelectedFile(null); }}
          onUpdated={() => loadFolder(currentPath)}
          showToast={showToast}
        />
      )}
      {showRenameModal && selectedFile && (
        <RenameModal
          file={selectedFile}
          onClose={() => { setShowRenameModal(false); setSelectedFile(null); }}
          onRenamed={() => loadFolder(currentPath)}
          showToast={showToast}
        />
      )}

      {/* Custom Confirm Dialog */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        danger={confirmModal.danger}
        confirmText="Delete"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false })}
      />

      {/* Context Menu */}
      {contextMenu.show && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          item={contextMenu.item}
          onAction={handleContextAction}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`ctodocx-toast ${toast.type}`}>
          <Icon name={toast.type === "success" ? "checkCircle" : toast.type === "error" ? "xCircle" : toast.type === "warning" ? "warning" : "info"} size={18} />
          <span>{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>
            <Icon name="x" size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "";
  const b = Number(bytes);
  if (isNaN(b)) return "";
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(1) + " MB";
}

function parseSize(s) {
  if (!s) return 0;
  const num = parseFloat(s);
  if (String(s).includes("MB")) return num * 1024;
  if (String(s).includes("KB")) return num;
  return num;
}

export default CTODOCXApp;
